import type { FieldEditorValues, SectionEditorValues } from './config';
import type { FieldSummary } from './constants';

export interface VisibilityContext {
  fieldConfigs: Record<string, FieldEditorValues>;
  sectionConfigs: Record<string, SectionEditorValues>;
  previewData: Record<string, unknown>;
  fields: Record<string, FieldSummary>;
  containers: Record<string, string[]>;
}

export const evaluateRules = (
  rules: { ifField?: string; values?: string[] }[],
  ctx: VisibilityContext,
  visiting: Set<string>
): boolean =>
  rules.every((rule) => {
    // If the trigger field is itself hidden, treat rule as unsatisfied
    if (
      rule.ifField &&
      !isFieldVisible({ fieldId: rule.ifField, ...ctx }, visiting)
    ) {
      return false;
    }

    const val = rule.ifField ? ctx.previewData[rule.ifField] : undefined;
    const values = rule.values ?? [];
    if (!values.length) {
      return true;
    }
    if (Array.isArray(val)) {
      return values.some((v) => val.includes(v));
    }

    return values.includes(String(val ?? ''));
  });

export const isFieldVisible = (
  { fieldId, ...ctx }: VisibilityContext & { fieldId: string },
  visiting: Set<string> = new Set()
): boolean => {
  // Cycle detected — treat as visible to break infinite recursion
  if (visiting.has(`field:${fieldId}`)) {
    return true;
  }
  visiting.add(`field:${fieldId}`);

  // If the field's section is hidden, the field is hidden
  const sectionId = Object.entries(ctx.containers).find(([, fieldIds]) =>
    fieldIds.includes(fieldId)
  )?.[0];
  if (sectionId && !isSectionVisible({ sectionId, ...ctx }, visiting)) {
    return false;
  }

  const config = ctx.fieldConfigs[fieldId];
  if (
    !config?.conditionalLogicEnabled ||
    !config.conditionalLogicRules?.length
  ) {
    return true;
  }

  return evaluateRules(config.conditionalLogicRules, ctx, visiting);
};

export const isSectionVisible = (
  { sectionId, ...ctx }: VisibilityContext & { sectionId: string },
  visiting: Set<string> = new Set()
): boolean => {
  // Cycle detected — treat as visible to break infinite recursion
  if (visiting.has(`section:${sectionId}`)) {
    return true;
  }
  visiting.add(`section:${sectionId}`);

  const config = ctx.sectionConfigs[sectionId];
  if (
    !config?.conditionalLogicEnabled ||
    !config.conditionalLogicRules?.length
  ) {
    return true;
  }

  return evaluateRules(config.conditionalLogicRules, ctx, visiting);
};

export const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(value);

    return Boolean(url);
  } catch {
    return false;
  }
};

export const isValueEmpty = (value: unknown): boolean => {
  if (value == null || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};
